"use client";

// oxlint-disable-next-line design-system/no-legacy-import -- a menu trigger the size of its label; the Button atom's min-width would widen it
import { extendedButtonVariants } from "@/components/atoms/Button/helpers";
import { Icon } from "@/components/atoms/Icon/Icon";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/molecules/DropdownMenu/DropdownMenu";
import { cn } from "@/lib/utils";
import {
  ArrowDown01Icon,
  FolderAddIcon,
  Loading03Icon,
  Upload03Icon,
} from "@hugeicons/core-free-icons";
import { FolderFormDialog } from "../WorkspaceFolders/FolderFormDialog";
import { useNewMenu } from "./useNewMenu";

interface Props {
  selectedFolderId: string | null;
  /** Open folder's name, shown in the create dialog so the destination is
   *  visible before the folder is made. */
  selectedFolderName?: string | null;
}

export function NewMenu({ selectedFolderId, selectedFolderName }: Props) {
  const {
    fileInputRef,
    isUploading,
    openFilePicker,
    handleFilesSelected,
    isCreateOpen,
    setIsCreateOpen,
    isCreating,
    handleCreateFolder,
  } = useNewMenu(selectedFolderId);

  return (
    <>
      <DropdownMenu data-sentry-element="DropdownMenu" data-sentry-source-file="NewMenu.tsx">
        <DropdownMenuTrigger asChild data-sentry-element="DropdownMenuTrigger" data-sentry-source-file="NewMenu.tsx">
          <button
            type="button"
            disabled={isUploading}
            className={cn(
              extendedButtonVariants({ variant: "primary", size: "small" }),
              "min-w-0 gap-1.5 pr-3 pl-4",
            )}
            data-testid="artifacts-new-menu"
          >
            {isUploading ? (
              <span className="flex animate-spin">
                <Icon icon={Loading03Icon} size={16} />
              </span>
            ) : null}
            {isUploading ? "Uploading…" : "New"}
            <Icon icon={ArrowDown01Icon} size={16} data-sentry-element="Icon" data-sentry-source-file="NewMenu.tsx" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-44" data-sentry-element="DropdownMenuContent" data-sentry-source-file="NewMenu.tsx">
          <DropdownMenuItem
            onSelect={openFilePicker}
            data-testid="artifacts-upload-file"
          data-sentry-element="DropdownMenuItem" data-sentry-source-file="NewMenu.tsx">
            <Icon icon={Upload03Icon} size={16} className="mr-2" data-sentry-element="Icon" data-sentry-source-file="NewMenu.tsx" />
            Upload file
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={() => setIsCreateOpen(true)}
            data-testid="create-folder-button"
          data-sentry-element="DropdownMenuItem" data-sentry-source-file="NewMenu.tsx">
            <Icon icon={FolderAddIcon} size={16} className="mr-2" data-sentry-element="Icon" data-sentry-source-file="NewMenu.tsx" />
            New folder
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="hidden"
        tabIndex={-1}
        aria-hidden
        onChange={handleFilesSelected}
        data-testid="artifacts-upload-input"
      />
      <FolderFormDialog
        isOpen={isCreateOpen}
        setIsOpen={setIsCreateOpen}
        mode="create"
        location={
          selectedFolderId ? (selectedFolderName ?? undefined) : undefined
        }
        isSubmitting={isCreating}
        onSubmit={handleCreateFolder}
      data-sentry-element="FolderFormDialog" data-sentry-source-file="NewMenu.tsx" />
    </>
  );
}
